import type { RunResponse } from "../../gen/models";

export interface RunTokens {
  inTok: number;
  outTok: number;
  total: number;
  cached: number;
}

export interface RunMetadata {
  rulesCount: number;
  isBlocked: boolean;
  isSimulation: boolean;
  isTestRun: boolean;
  testRunId?: string;
  snapshotId?: string;
  snapshotName?: string;
  errorMessage?: string;
}

export function formatModelName(raw?: string): string | null {
  if (!raw || typeof raw !== "string") return null;
  return raw.replace(/^(google|anthropic|openai|meta-llama|deepseek)\//, "");
}

export function formatDuration(ms?: number): string | null {
  if (ms === undefined || ms === null || isNaN(ms) || ms < 0) return null;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60000);
  const s = Math.round((ms % 60000) / 1000);
  return `${m}m ${s}s`;
}

export function formatCost(costUsd?: number): string {
  if (costUsd === undefined || costUsd === null || isNaN(costUsd) || costUsd <= 0) return "$0.00";
  if (costUsd < 0.0001) return "<$0.0001";
  if (costUsd < 0.01) return `$${costUsd.toFixed(4)}`;
  return `$${costUsd.toFixed(3)}`;
}

export function formatTokens(num?: number): string {
  if (!num || isNaN(num) || num <= 0) return "0";
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}k`;
  return num.toLocaleString();
}

export function extractRunModel(r: RunResponse): string | undefined {
  const t: any = r.telemetry || {};
  const pRes: any = r.result_payload || {};
  const pTrig: any = r.trigger_payload || {};
  const pState: any = r.state || {};

  const raw =
    t.model ||
    t.model_name ||
    (r as any).model ||
    (r as any).model_name ||
    pRes.model ||
    pRes.model_name ||
    pRes.quote_draft?.model ||
    pRes.quote_draft?.model_name ||
    pRes.draft?.model ||
    pRes.draft?.model_name ||
    pRes.metadata?.model ||
    pRes.metadata?.model_name ||
    pRes.diagnostics?.model ||
    pState.model ||
    pState.model_name ||
    pTrig.model ||
    pTrig.model_name;

  return formatModelName(raw) || undefined;
}

export function formatProviderName(raw?: string): string | null {
  if (!raw || typeof raw !== "string") return null;
  const cleaned = raw.trim().toLowerCase();
  if (cleaned === "google" || cleaned === "gemini") return "Google";
  if (cleaned === "openrouter") return "OpenRouter";
  if (cleaned === "anthropic") return "Anthropic";
  if (cleaned === "openai") return "OpenAI";
  if (cleaned === "deepseek") return "DeepSeek";
  if (cleaned === "groq") return "Groq";
  if (cleaned === "together" || cleaned === "togetherai") return "Together";
  if (cleaned === "fireworks" || cleaned === "fireworksai") return "Fireworks";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function extractRunProvider(r: RunResponse): string | undefined {
  const t: any = r.telemetry || {};
  const pRes: any = r.result_payload || {};
  const pTrig: any = r.trigger_payload || {};
  const pState: any = r.state || {};

  const raw =
    t.provider ||
    pRes.provider ||
    pRes.quote_draft?.provider ||
    pRes.draft?.provider ||
    pRes.metadata?.provider ||
    pRes.diagnostics?.provider ||
    pState.provider ||
    pTrig.provider;

  if (raw) {
    return formatProviderName(raw) || undefined;
  }

  // Infer provider from model name if not explicitly set
  const model =
    t.model ||
    t.model_name ||
    pRes.model ||
    pRes.model_name ||
    pRes.quote_draft?.model ||
    pRes.draft?.model ||
    pState.model ||
    pTrig.model;

  if (typeof model === "string") {
    if (model.includes("/") || model.startsWith("openrouter:")) return "OpenRouter";
    if (model.toLowerCase().startsWith("gemini-")) return "Google";
  }

  return undefined;
}

export function extractRunDuration(r: RunResponse): number | undefined {
  const t: any = r.telemetry || {};
  const pRes: any = r.result_payload || {};

  if (t.duration_ms !== undefined && t.duration_ms !== null) {
    const ms = Number(t.duration_ms);
    if (!isNaN(ms) && ms > 0) return ms;
  }
  if (t.latency_ms !== undefined && t.latency_ms !== null) {
    const ms = Number(t.latency_ms);
    if (!isNaN(ms) && ms > 0) return ms;
  }
  if (t.duration_seconds !== undefined && t.duration_seconds !== null) {
    const s = Number(t.duration_seconds);
    if (!isNaN(s) && s > 0) return Math.round(s * 1000);
  }
  if (pRes.diagnostics?.duration_seconds !== undefined && pRes.diagnostics?.duration_seconds !== null) {
    const s = Number(pRes.diagnostics.duration_seconds);
    if (!isNaN(s) && s > 0) return Math.round(s * 1000);
  }
  if (pRes.latency_ms !== undefined && pRes.latency_ms !== null) {
    const ms = Number(pRes.latency_ms);
    if (!isNaN(ms) && ms > 0) return ms;
  }
  if (r.completed_at && r.started_at) {
    const start = new Date(r.started_at).getTime();
    const end = new Date(r.completed_at).getTime();
    if (!isNaN(start) && !isNaN(end) && end >= start) {
      return end - start;
    }
  }
  return undefined;
}

export function extractRunCost(r: RunResponse): number {
  const t: any = r.telemetry || {};
  const pRes: any = r.result_payload || {};
  const cost = Number(
    t.cost_usd ??
    t.estimated_cost_usd ??
    pRes.cost_usd ??
    pRes.estimated_cost_usd ??
    pRes.telemetry?.cost_usd ??
    pRes.telemetry?.estimated_cost_usd ??
    0
  );
  return isNaN(cost) ? 0 : cost;
}

export function extractRunTurns(r: RunResponse): number | undefined {
  const t: any = r.telemetry || {};
  const pRes: any = r.result_payload || {};
  const pState: any = r.state || {};

  const rawTurns =
    t.turns ??
    t.turn_count ??
    t.llm_requests ??
    pRes.diagnostics?.llm_requests ??
    pRes.turns ??
    pState.turns ??
    pState.turn_count ??
    pState.iterations;

  if (rawTurns !== undefined && rawTurns !== null) {
    const num = Number(rawTurns);
    if (!isNaN(num) && num > 0) return num;
  }
  if (r.status === "completed") return 1;
  return undefined;
}

export function extractRunTools(r: RunResponse): string[] {
  const toolsSet = new Set<string>();
  const pRes: any = r.result_payload || {};
  const t: any = r.telemetry || {};
  const pState: any = r.state || {};

  const addTool = (val: any) => {
    if (!val) return;
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (trimmed && trimmed !== "[object Object]") {
        toolsSet.add(trimmed.replace(/^['"]|['"]$/g, ""));
      }
    } else if (typeof val === "object") {
      const name = val.tool_name || val.toolKey || val.tool || val.name || val.title;
      if (typeof name === "string" && name.trim()) {
        toolsSet.add(name.trim().replace(/^['"]|['"]$/g, ""));
      }
    }
  };

  const resTools = pRes.tools_called || pRes.tools || pRes.tool_names;
  if (Array.isArray(resTools)) resTools.forEach(addTool);

  const telTools = t.tools_called || t.tools || t.tool_names;
  if (Array.isArray(telTools)) telTools.forEach(addTool);

  const stateTools = pState.tools_called || pState.tools || pState.tool_names;
  if (Array.isArray(stateTools)) stateTools.forEach(addTool);

  if (Array.isArray(pRes.executed_queries) && pRes.executed_queries.length > 0) {
    toolsSet.add("execute_sql");
  }

  if (Array.isArray(pRes.sources_consulted_details)) {
    pRes.sources_consulted_details.forEach((s: any) => {
      if (s && (s.isTool || s.toolKey)) {
        addTool(s.toolKey || s.title || s.tool);
      }
    });
  }

  if (Array.isArray(pRes.sources_consulted)) {
    pRes.sources_consulted.forEach((s: any) => {
      if (typeof s === "string" && s.toLowerCase().includes("rates engine")) {
        toolsSet.add("check_live_rates");
      }
    });
  }

  return Array.from(toolsSet);
}

export function extractRunTokens(r: RunResponse): RunTokens {
  const t: any = r.telemetry || {};
  const pRes: any = r.result_payload || {};
  const inTok = Number(t.tokens_in ?? t.input_tokens ?? pRes.diagnostics?.input_tokens ?? 0);
  const outTok = Number(t.tokens_out ?? t.output_tokens ?? pRes.diagnostics?.output_tokens ?? 0);
  const total = Number(t.total_tokens ?? (inTok + outTok) ?? pRes.diagnostics?.total_tokens ?? 0);
  const cached = Number(t.cached_tokens ?? t.cache_read_tokens ?? t.prompt_tokens_cached ?? 0);
  return { inTok, outTok, total, cached };
}

export function extractRunMetadata(r: RunResponse): RunMetadata {
  const pRes: any = r.result_payload || {};
  const pState: any = r.state || {};
  const rulesCount = Number(
    pRes.rules_applied?.length ??
    pRes.rules_applied_details?.length ??
    pState.rules_applied?.length ??
    0
  );
  const isBlocked = Boolean(
    r.status === "blocked" ||
    pState.guardrail_blocked ||
    pRes.guardrail_blocked ||
    pRes.guardrail_flag
  );
  const isSimulation = Boolean(
    r.status === "simulation" ||
    pState.is_simulation
  );
  const isTestRun = Boolean(
    r.is_test ||
    r.test_run_id ||
    pState.is_test ||
    pState.test_run_id ||
    pRes.test_run_id ||
    (r.trigger_payload as any)?.test_run_id ||
    (r.trigger_payload as any)?.is_test ||
    (r.telemetry as any)?.test_run_id ||
    (r.telemetry as any)?.is_test
  );
  const testRunId =
    r.test_run_id ||
    pState.test_run_id ||
    pRes.test_run_id ||
    (r.trigger_payload as any)?.test_run_id ||
    (r.telemetry as any)?.test_run_id ||
    undefined;
  const snapshotId =
    r.environment_snapshot_id ||
    pState.snapshot_id ||
    pState.environment_snapshot_id ||
    (r.trigger_payload as any)?.snapshot_id ||
    (r.trigger_payload as any)?.environment_snapshot_id ||
    (r.telemetry as any)?.snapshot_id ||
    undefined;
  const snapshotName =
    r.snapshot_name ||
    pState.snapshot_name ||
    (r.trigger_payload as any)?.snapshot_name ||
    (r.telemetry as any)?.snapshot_name ||
    undefined;
  const errorMessage =
    r.status === "failed"
      ? (pRes.error || pRes.error_message || pRes.detail || pState.error || undefined)
      : undefined;

  return { rulesCount, isBlocked, isSimulation, isTestRun, testRunId, snapshotId, snapshotName, errorMessage };
}
